import React, { useEffect, useMemo, useState } from "react";
import FlightProgress from "../layout/FlightProgress";
import StickyBookingSummary from "../details/StickyBookingSummary";
import { getMockPaymentViewModel } from "./paymentMockData";
import "./PaymentScreen.css";

/**
 * Payment Design Preview — hosted card / bank / hold / split / status.
 * No provider / API / real charges. Amounts come from ViewModel only.
 */
export default function PaymentScreen({
  offer,
  search,
  payment: paymentProp = null,
  initialMethodId = "card",
  onContinue,
  onBack,
}) {
  const payment = useMemo(
    () => paymentProp || getMockPaymentViewModel(offer, initialMethodId),
    [paymentProp, offer, initialMethodId]
  );

  const [actionId, setActionId] = useState(payment.selectedActionId || "card");
  const [status, setStatus] = useState(payment.status);
  const [splitOpen, setSplitOpen] = useState(
    Boolean(payment.split?.initiallyOpen)
  );
  const [splitCards, setSplitCards] = useState(() =>
    (payment.split?.cards || []).map((c) => ({ ...c }))
  );

  useEffect(() => {
    setActionId(payment.selectedActionId || "card");
    setStatus(payment.status);
    setSplitOpen(Boolean(payment.split?.initiallyOpen));
    setSplitCards((payment.split?.cards || []).map((c) => ({ ...c })));
  }, [payment]);

  const derivedOffer = useMemo(
    () => ({
      ...offer,
      bookingSummary: payment.bookingSummary,
    }),
    [offer, payment.bookingSummary]
  );

  const activeSplitIndex = splitCards.findIndex((c) => c.status !== "paid");
  const splitActive =
    actionId === "card" && splitOpen && status == null;
  const currentSplitCard =
    splitActive && activeSplitIndex >= 0 ? splitCards[activeSplitIndex] : null;

  const stickyCta = (() => {
    if (status === "processing") {
      return { continueLabel: "מעבד...", backLabel: "חזרה לסיכום", disabled: true };
    }
    if (status === "success") {
      return {
        continueLabel: "המשך לאישור",
        backLabel: "חזרה לסיכום",
        disabled: false,
      };
    }
    if (status === "failed") {
      return {
        continueLabel: payment.statusCopy.failed.retryLabel,
        backLabel: "חזרה לסיכום",
        disabled: false,
      };
    }
    if (actionId === "hold") {
      return {
        continueLabel: payment.moreOptions.hold.ctaLabel,
        backLabel: "חזרה לסיכום",
        disabled: false,
      };
    }
    if (actionId === "bank") {
      return {
        continueLabel: payment.bankDetails.ctaLabel,
        backLabel: "חזרה לסיכום",
        disabled: false,
      };
    }
    if (currentSplitCard) {
      return {
        continueLabel: currentSplitCard.payLabel,
        backLabel: "חזרה לסיכום",
        disabled: false,
      };
    }
    if (actionId === "card" && payment.cardMode === "embedded") {
      return {
        continueLabel: payment.embeddedCard.ctaLabel,
        backLabel: "חזרה לסיכום",
        disabled: false,
      };
    }
    return {
      continueLabel: payment.hostedCard.ctaLabel,
      backLabel: "חזרה לסיכום",
      disabled: false,
    };
  })();

  const handleStickyContinue = () => {
    if (status === "failed") {
      setStatus(null);
      return;
    }
    if (status === "success") {
      if (typeof onContinue === "function") onContinue({ actionId });
      return;
    }
    if (status === "processing") return;

    if (currentSplitCard) {
      setSplitCards((prev) =>
        prev.map((c, i) =>
          i === activeSplitIndex ? { ...c, status: "paid" } : c
        )
      );
      return;
    }

    if (typeof onContinue === "function") onContinue({ actionId });
  };

  const handleRetry = () => setStatus(null);
  const handleOtherMethod = () => {
    setStatus(null);
    setActionId("bank");
    setSplitOpen(false);
  };

  const selectPayMethod = (id) => {
    if (status === "processing") return;
    setStatus(null);
    setActionId(id);
    if (id !== "card") setSplitOpen(false);
  };

  const selectHold = () => {
    if (status === "processing") return;
    setStatus(null);
    setActionId("hold");
    setSplitOpen(false);
  };

  const allSplitPaid =
    splitCards.length > 0 && splitCards.every((c) => c.status === "paid");

  return (
    <div className="fe2-pay-page">
      <header className="fe2-pay-head">
        <div className="fe2-pay-head__text">
          <h1 className="fe2-pay-head__title">{payment.title}</h1>
        </div>
        <button type="button" className="fe2-pay-head__back" onClick={onBack}>
          ← חזרה לסיכום
        </button>
      </header>

      <FlightProgress activeStep="payment" />

      <div className="fe2-pay-page__grid">
        <div className="fe2-pay-page__main">
          {status ? (
            <StatusPanel
              status={status}
              copy={payment.statusCopy}
              onRetry={handleRetry}
              onOtherMethod={handleOtherMethod}
            />
          ) : null}

          {!status || status === "failed" ? (
            <>
              <section className="fe2-pay-now" aria-label={payment.payable.caption}>
                <p className="fe2-pay-now__caption">{payment.payable.caption}</p>
                <p className="fe2-pay-now__amount fe2-ltr">
                  {payment.payable.amountLabel}
                </p>
              </section>

              {status !== "failed" ? (
                <>
                  <section
                    className="fe2-pay-methods"
                    aria-label={payment.methodsTitle}
                  >
                    <h2 className="fe2-pay-methods__title">
                      {payment.methodsTitle}
                    </h2>
                    <div className="fe2-pay-methods__grid" role="radiogroup">
                      {(payment.methods || []).map((method) => {
                        const selected = actionId === method.id;
                        return (
                          <button
                            key={method.id}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            className={
                              selected
                                ? "fe2-pay-choice fe2-pay-choice--selected"
                                : "fe2-pay-choice"
                            }
                            onClick={() => selectPayMethod(method.id)}
                          >
                            {method.label}
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  {actionId === "card" ? (
                    <>
                      {payment.cardMode === "embedded" ? (
                        <EmbeddedCardPanel form={payment.embeddedCard} />
                      ) : (
                        <HostedCardPanel hosted={payment.hostedCard} />
                      )}

                      <SplitPaymentPanel
                        split={payment.split}
                        open={splitOpen}
                        cards={splitCards}
                        allPaid={allSplitPaid}
                        activeIndex={activeSplitIndex}
                        onToggle={() => setSplitOpen((v) => !v)}
                        onPayCard={(index) => {
                          setSplitCards((prev) =>
                            prev.map((c, i) =>
                              i === index ? { ...c, status: "paid" } : c
                            )
                          );
                        }}
                      />
                    </>
                  ) : null}

                  {actionId === "bank" ? (
                    <BankTransferPanel bank={payment.bankDetails} />
                  ) : null}

                  {payment.holdEligible ? (
                    <>
                      <div className="fe2-pay-divider" aria-hidden />
                      <HoldOptionPanel
                        more={payment.moreOptions}
                        selected={actionId === "hold"}
                        onSelect={selectHold}
                      />
                    </>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="fe2-pay-page__side">
          <StickyBookingSummary
            offer={derivedOffer}
            search={search}
            onContinue={handleStickyContinue}
            onBack={onBack}
            continueLabel={stickyCta.continueLabel}
            backLabel={stickyCta.backLabel}
            continueDisabled={Boolean(stickyCta.disabled)}
          />
        </div>
      </div>
    </div>
  );
}

function StatusPanel({ status, copy, onRetry, onOtherMethod }) {
  const block = copy?.[status];
  if (!block) return null;

  return (
    <section
      className={`fe2-pay-status fe2-pay-status--${status}`}
      role="status"
      aria-live="polite"
    >
      <h2 className="fe2-pay-status__title">{block.title}</h2>
      <p className="fe2-pay-status__body">{block.body}</p>
      {status === "failed" ? (
        <div className="fe2-pay-status__actions">
          <button
            type="button"
            className="fe2-pay-status__btn fe2-pay-status__btn--primary"
            onClick={onRetry}
          >
            {block.retryLabel}
          </button>
          <button
            type="button"
            className="fe2-pay-status__btn fe2-pay-status__btn--secondary"
            onClick={onOtherMethod}
          >
            {block.otherMethodLabel}
          </button>
        </div>
      ) : null}
      {status === "processing" ? (
        <div className="fe2-pay-status__spinner" aria-hidden />
      ) : null}
    </section>
  );
}

function HostedCardPanel({ hosted }) {
  return (
    <section className="fe2-pay-panel" aria-label={hosted.title}>
      <h2 className="fe2-pay-panel__title">{hosted.title}</h2>
      <div className="fe2-pay-hosted">
        <div className="fe2-pay-hosted__amount-row">
          <span className="fe2-pay-hosted__caption">{hosted.amountCaption}</span>
          <strong className="fe2-pay-hosted__amount fe2-ltr">
            {hosted.amountLabel}
          </strong>
        </div>
        <p className="fe2-pay-hosted__secure">{hosted.secureNote}</p>
      </div>
    </section>
  );
}

function EmbeddedCardPanel({ form }) {
  return (
    <section className="fe2-pay-panel" aria-label={form.title}>
      <header className="fe2-pay-panel__head">
        <h2 className="fe2-pay-panel__title">{form.title}</h2>
        <span className="fe2-pay-panel__badge">{form.previewBadge}</span>
      </header>
      <p className="fe2-pay-panel__mock">{form.mockNote}</p>
      <div className="fe2-pay-card-grid">
        {(form.fields || []).map((field) => (
          <label
            key={field.id}
            className={
              field.id === "cardNumber" || field.id === "cardHolder"
                ? "fe2-pay-field fe2-pay-field--wide"
                : "fe2-pay-field"
            }
          >
            <span className="fe2-pay-field__label">{field.label}</span>
            <input
              className={
                field.ltr ? "fe2-pay-field__input fe2-ltr" : "fe2-pay-field__input"
              }
              type="text"
              dir={field.ltr ? "ltr" : undefined}
              readOnly
              value={field.value}
              placeholder={field.placeholder}
              aria-readonly="true"
            />
          </label>
        ))}
      </div>
    </section>
  );
}

function SplitPaymentPanel({
  split,
  open,
  cards,
  allPaid,
  activeIndex,
  onToggle,
  onPayCard,
}) {
  if (!split) return null;

  return (
    <section className="fe2-pay-split" aria-label={split.toggleLabel}>
      <button
        type="button"
        className={
          open
            ? "fe2-pay-split__toggle fe2-pay-split__toggle--open"
            : "fe2-pay-split__toggle"
        }
        aria-expanded={open}
        onClick={onToggle}
      >
        <span>{split.toggleLabel}</span>
        <span className="fe2-pay-split__chevron" aria-hidden>
          {open ? "▾" : "◂"}
        </span>
      </button>

      {open ? (
        <div className="fe2-pay-split__body">
          <div className="fe2-pay-split__progress">
            <p className="fe2-pay-split__progress-paid">{split.progressPaidLabel}</p>
            <p className="fe2-pay-split__progress-left">
              {split.progressRemainingLabel}
            </p>
          </div>

          <ul className="fe2-pay-split__cards">
            {cards.map((card, index) => {
              const isActive = index === activeIndex && card.status !== "paid";
              const isPaid = card.status === "paid";
              const isLocked = !isPaid && index > activeIndex;
              return (
                <li
                  key={card.id}
                  className={
                    isPaid
                      ? "fe2-pay-split__card fe2-pay-split__card--paid"
                      : isActive
                        ? "fe2-pay-split__card fe2-pay-split__card--active"
                        : "fe2-pay-split__card fe2-pay-split__card--locked"
                  }
                >
                  <div className="fe2-pay-split__card-top">
                    <strong>{card.label}</strong>
                    <span className="fe2-ltr">{card.amountLabel}</span>
                  </div>
                  {isPaid ? (
                    <p className="fe2-pay-split__paid">{card.paidLabel}</p>
                  ) : isActive ? (
                    <button
                      type="button"
                      className="fe2-pay-split__pay-btn"
                      onClick={() => onPayCard(index)}
                    >
                      {card.payLabel}
                    </button>
                  ) : isLocked ? (
                    <p className="fe2-pay-split__locked">ממתין לכרטיס הקודם</p>
                  ) : null}
                </li>
              );
            })}
          </ul>

          <div className="fe2-pay-split__totals">
            <div className="fe2-pay-split__total-row">
              <span>{split.totalLabel}</span>
              <strong className="fe2-ltr">{split.totalAmountLabel}</strong>
            </div>
            <div className="fe2-pay-split__total-row fe2-pay-split__total-row--muted">
              <span>{split.remainingLabel}</span>
              <strong className="fe2-ltr">{split.remainingAmountLabel}</strong>
            </div>
          </div>

          <button type="button" className="fe2-pay-split__add" disabled={allPaid}>
            {split.addCardLabel}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function BankTransferPanel({ bank }) {
  return (
    <section className="fe2-pay-panel" aria-label={bank.title}>
      <h2 className="fe2-pay-panel__title">{bank.title}</h2>
      <div className="fe2-pay-hosted__amount-row fe2-pay-hosted__amount-row--bank">
        <span className="fe2-pay-hosted__caption">{bank.amountCaption}</span>
        <strong className="fe2-pay-hosted__amount fe2-ltr">{bank.amountLabel}</strong>
      </div>
      <h3 className="fe2-pay-panel__sub">{bank.accountTitle}</h3>
      <dl className="fe2-pay-detail-list">
        {(bank.lines || []).map((line) => (
          <div key={line.label} className="fe2-pay-detail-row">
            <dt>{line.label}</dt>
            <dd className="fe2-ltr">{line.value}</dd>
          </div>
        ))}
      </dl>
      <div className="fe2-pay-bank-next">
        <h3 className="fe2-pay-panel__sub">{bank.nextStepTitle}</h3>
        <p className="fe2-pay-panel__note">{bank.nextStepNote}</p>
      </div>
    </section>
  );
}

function HoldOptionPanel({ more, selected, onSelect }) {
  const hold = more?.hold;
  if (!hold) return null;

  return (
    <section className="fe2-pay-more" aria-label={more.title}>
      <h2 className="fe2-pay-more__title">{more.title}</h2>
      <button
        type="button"
        className={
          selected ? "fe2-pay-hold fe2-pay-hold--selected" : "fe2-pay-hold"
        }
        aria-pressed={selected}
        onClick={onSelect}
      >
        <strong className="fe2-pay-hold__label">{hold.label}</strong>
        <span className="fe2-pay-hold__until">{hold.availableUntilLabel}</span>
        <dl className="fe2-pay-hold__facts">
          <div>
            <dt>{hold.priceGuaranteedLabel}</dt>
            <dd>{hold.priceGuaranteedValue}</dd>
          </div>
          <div>
            <dt>{hold.afterExpiryLabel}</dt>
            <dd>{hold.afterExpiryValue}</dd>
          </div>
          <div>
            <dt>{hold.paymentLaterLabel}</dt>
            <dd>{hold.paymentLaterValue}</dd>
          </div>
        </dl>
      </button>
    </section>
  );
}
